import { IPaymentCard } from "../../Orchestrator/Types/Interface/IPaymentCards";

/**
 * Cards already stored on the customer's account (GET /customers/{id}/payment-cards)
 * are offered as payment methods alongside "new credit/debit card". A stored card
 * is encoded into the payment-method radio value so it flows through the existing
 * paymentStateAtom untouched; RedirectToPaymentGatewayAction decodes it back into
 * the `cardId` it sends on the gateway session call.
 */
const STORED_CARD_PREFIX = "card:";

/** Radio value representing a stored card. */
export const storedCardValue = (cardId: string): string => `${STORED_CARD_PREFIX}${cardId}`;

/** The card id inside a payment-method value, or "" if it isn't a stored card. */
export const parseStoredCardId = (method: string | null | undefined): string =>
    method?.startsWith(STORED_CARD_PREFIX) ? method.slice(STORED_CARD_PREFIX.length) : "";

/**
 * How a stored card is identified to the customer, everywhere it is shown:
 * `**** 0008`. The API's own mask (`512345xxxxxx0008`) still carries the issuer
 * digits, so re-mask here rather than printing it through — the last four are
 * all the customer needs to tell their cards apart.
 */
export const formatMaskedPan = (maskedPan: string | null | undefined): string => {
    const pan = String(maskedPan ?? "");
    return pan.length > 4 ? `**** ${pan.slice(-4)}` : pan;
};

/**
 * Brand logo for a card, at the intrinsic size the asset was exported at. Every
 * surface renders these centred in the same 50x36 slot, so a card looks the
 * same at checkout as it does under My Account.
 *
 * Matched by substring because the API brands cards loosely (`VISA`, but also
 * `VISA_DEBIT` and similar); an unrecognised brand falls back to a generic card.
 */
export interface ICardLogo {
    src: string;
    width: number;
    height: number;
}

export const GENERIC_CARD_LOGO: ICardLogo = {
    src: "/assets/checkout/creditcard.svg",
    width: 32,
    height: 32,
};

const BRAND_LOGOS: ReadonlyArray<[string, ICardLogo]> = [
    ["VISA", { src: "/assets/checkout/visacard.svg", width: 40, height: 13 }],
    ["MASTERCARD", { src: "/assets/checkout/mastercard.svg", width: 42, height: 30 }],
];

export const cardBrandLogo = (brand: string | null | undefined): ICardLogo => {
    const name = (brand || "").toUpperCase();
    return BRAND_LOGOS.find(([key]) => name.includes(key))?.[1] ?? GENERIC_CARD_LOGO;
};

/** Payment-method value for paying with a new card. */
export const NEW_CARD_METHOD = "credit";

/**
 * Which payment method the checkout should start on, given the stored cards the
 * customer can actually use.
 *
 * The card flagged `isDefault` by the API wins; otherwise we fall back to a new
 * card. Callers pass the ALREADY-FILTERED usable list, so a default card that
 * has expired simply isn't there and the customer lands on "new card" instead of
 * a preselection that could only fail at the gateway.
 *
 * Deliberately does NOT fall back to "first stored card" when no card is
 * flagged: with no default set, silently charging whichever card the API happens
 * to list first is a worse outcome than making the customer pick.
 */
export const preferredPaymentMethod = (usableCards: readonly IPaymentCard[]): string => {
    const defaultCard = usableCards.find((card) => card.isDefault);
    return defaultCard ? storedCardValue(defaultCard.id) : NEW_CARD_METHOD;
};

/**
 * A card is usable through the end of its expiry month. An expired card can only
 * ever fail at the gateway, so it is never offered.
 */
export const isCardUsable = (card: IPaymentCard, now: Date = new Date()): boolean => {
    const year = now.getFullYear();
    return card.expYear > year || (card.expYear === year && card.expMonth >= now.getMonth() + 1);
};
